<script lang="ts">
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';
	import { page } from '$app/stores';

	// Track if scripts are loaded
	let chatLoaded = false;
	let rezdyLoaded = false;
	let gaLoaded = false;

	// Configuration options
	const chatConfig = {
		mode: 'widget',
		chat2_base_url: '//tassietourscom.chat2.com/',
		wheight: 450,
		wwidth: 350,
		pheight: 520,
		pwidth: 500,
		leaveamessage: true,
		check_messages: true,
		lang: 'eng/'
	};

	// Enhanced page navigation tracking for analytics with minimal initial config
	$: if (browser && typeof window !== 'undefined' && window.gtag && $page && gaLoaded) {
		window.gtag('config', 'G-2XHLFCJCKR', {
			page_title: document.title,
			page_path: $page.url.pathname
		});
	}

	// Load the chat widget based on user engagement or scroll position
	function loadChatWidget() {
		if (!browser || chatLoaded) return;

		// Initialize Chat2 API
		window.CHAT2_API = window.CHAT2_API || {};
		window.CHAT2_API.args = chatConfig;

		// Create script element with performance attributes
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.setAttribute('crossorigin', 'anonymous');
		script.async = true;
		script.defer = true;
		script.fetchPriority = 'low'; // Add priority hint

		// Add timestamp for cache busting
		const date = new Date();
		script.src =
			'//tassietourscom.chat2.com/design/defaulttheme/js/widgetv2/index.js?' +
			('' + date.getFullYear() + date.getMonth() + date.getDate());

		// Append to document
		document.body.appendChild(script);
		chatLoaded = true;
	}

	// Load Rezdy booking script with enhanced performance
	function loadRezdyScript() {
		if (!browser || rezdyLoaded) return;

		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.src = 'https://tassietours.rezdy.com/pluginJs?script=modal';
		script.async = true;
		script.defer = true;

		// Add priority hint for non-critical script
		script.fetchPriority = 'low';

		document.body.appendChild(script);
		rezdyLoaded = true;
	}

	// Initialize analytics with minimal configuration
	function initializeGA() {
		if (!browser || gaLoaded || typeof window.gtag !== 'undefined') return;

		// Create and load the GA script
		const script = document.createElement('script');
		script.src = 'https://www.googletagmanager.com/gtag/js?id=G-2XHLFCJCKR';
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);

		// Initialize dataLayer
		window.dataLayer = window.dataLayer || [];
		window.gtag = function () {
			window.dataLayer.push(arguments);
		};

		// Set minimal initial config to reduce early main thread work
		window.gtag('js', new Date());
		window.gtag('config', 'G-2XHLFCJCKR', {
			send_page_view: false, // Defer page view tracking
			anonymize_ip: true // Privacy enhancement
		});

		gaLoaded = true;

		// Set full configuration after a short delay
		setTimeout(() => {
			window.gtag('config', 'G-2XHLFCJCKR', {
				page_title: document.title,
				page_path: $page.url.pathname,
				send_page_view: true
			});
		}, 2000);
	}

	// Set up a scroll-based chat widget loader
	function setupScrollBasedChatLoader() {
		if (!browser) return;

		const scrollTrigger = () => {
			// Load chat when user scrolls 70% down the page
			const scrollPercentage = window.scrollY / (document.body.scrollHeight - window.innerHeight);
			if (scrollPercentage > 0.7) {
				loadChatWidget();
				window.removeEventListener('scroll', scrollTrigger);
			}
		};

		window.addEventListener('scroll', scrollTrigger, { passive: true });
	}

	onMount(() => {
		// Initialize analytics with minimal impact
		initializeGA();

		// For Rezdy, load after a small delay or if URL suggests booking intent
		if ($page.url.pathname.includes('/tours/')) {
			// Load sooner on tour pages
			setTimeout(loadRezdyScript, 1500);
		} else {
			// Delay more on other pages
			setTimeout(loadRezdyScript, 3000);
		}

		// Set up scroll-based chat widget loading
		setupScrollBasedChatLoader();

		// Fallback for chat widget after longer delay
		const chatTimer = setTimeout(loadChatWidget, 10000);

		// User interaction can also trigger chat loading
		const interactionEvents = ['click', 'scroll', 'touchstart'];
		let interactionCount = 0;

		const handleInteraction = () => {
			interactionCount++;
			// Only load after several meaningful interactions
			if (interactionCount >= 3) {
				clearTimeout(chatTimer);
				loadChatWidget();
				// Remove listeners after loading
				interactionEvents.forEach((event) => {
					window.removeEventListener(event, handleInteraction);
				});
			}
		};

		// Add interaction listeners
		interactionEvents.forEach((event) => {
			window.addEventListener(event, handleInteraction, { passive: true });
		});

		// Clean up on component destruction
		return () => {
			clearTimeout(chatTimer);
			interactionEvents.forEach((event) => {
				window.removeEventListener(event, handleInteraction);
			});
		};
	});
</script>

<svelte:head>
	<!-- Resource hints for third-party domains -->
	<link rel="preconnect" href="https://www.googletagmanager.com" crossorigin />
	<link rel="preconnect" href="https://tassietours.rezdy.com" crossorigin />
	<link rel="preconnect" href="https://tassietourscom.chat2.com" crossorigin />
	<link rel="preconnect" href="https://api.rezdy.com" crossorigin />

	<!-- Defer Google Tag Manager noscript iframe -->
	<noscript>
		<iframe
			src="https://www.googletagmanager.com/ns.html?id=GTM-WH6X9SZ"
			height="0"
			width="0"
			style="display:none;visibility:hidden"
			title="Google Tag Manager"
			loading="lazy"
		></iframe>
	</noscript>
</svelte:head>
